<template>
  <div>
    <!-- Breadcrumb -->
    <div class="page-header">
      <div class="page-block">
        <div class="row align-items-center">
          <div class="col-md-12">
            <div class="page-header-title">
              <h5>{{ $t('rfq.title') }}</h5>
            </div>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">{{ $t('common.navigation.home') }}</router-link>
              </li>
              <li class="breadcrumb-item active">{{ $t('rfq.listTitle') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Tabs -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <ul class="nav nav-tabs invoice-tab border-bottom-0" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'all' }" @click="setActiveTab('all')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.all') }}
                      <span class="avtar rounded-circle bg-light-primary">{{ totalStatusCount }}</span>
                    </span>
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'waiting' }" @click="setActiveTab('waiting')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.waiting') }}
                      <span class="avtar rounded-circle bg-light-warning">{{ statusCounts.waiting || 0 }}</span>
                    </span>
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'submitted' }" @click="setActiveTab('submitted')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.submitted') }}
                      <span class="avtar rounded-circle bg-light-success">{{ statusCounts.submitted + statusCounts.readyToSend || 0 }}</span>
                    </span>
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'revision' }" @click="setActiveTab('revision')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.revision') }}
                      <span class="avtar rounded-circle bg-light-purple">{{ statusCounts.revision || 0 }}</span>
                    </span>
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'declined' }" @click="setActiveTab('declined')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.declined') }}
                      <span class="avtar rounded-circle bg-light-danger">{{ statusCounts.declined || 0 }}</span>
                    </span>
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" :class="{ active: activeTab === 'expired' }" @click="setActiveTab('expired')" type="button" role="tab">
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.expired') }}
                      <span class="avtar rounded-circle bg-light-orange">{{ statusCounts.expired || 0 }}</span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>

            <!-- Tab Panes -->
            <div class="tab-content" id="myTabContent">
              <div class="tab-pane fade show active" role="tabpanel" tabindex="0">
                <DataGrid
                  :data="requests" :columns="columns"
                  v-model:currentPage="currentPage" v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText" v-model:sortField="sortField" v-model:sortDirection="sortDirection"
                  :loading="isLoading" :error="error" :empty-state-message="emptyStateMessage"
                  :search-placeholder="$t('rfq.filters.keywordPlaceholder')" :total-items="totalItems"
                  :storage-key="'rfqList_columnWidths_v3'" :show-refresh="true"
                  @search="handleSearch" @pageSizeChange="handlePageSizeChange" @pageChange="handlePageChange"
                  @sort="handleSort" @refresh="fetchQuotations" @view="viewRequest" @edit="editRequest" @delete="deleteRequest"
                  :renderActions="renderActions"
                >
                  <template #filterActions>
                    <div class="filter-dropdown" ref="filterDropdownRef">
                      <button
                        class="btn btn-outline-secondary btn-sm position-relative"
                        type="button"
                        :class="{ active: showFilters }"
                        :title="$t('rfq.filters.toggleFilters')"
                        :aria-label="$t('rfq.filters.toggleFilters')"
                        :aria-expanded="showFilters"
                        @click="showFilters = !showFilters"
                        style="border-color: #ced4da; border-width: 1px;"
                      >
                        <i class="ti ti-filter" style="font-size: 0.875rem;"></i>
                        <span
                          v-if="hasActiveFilters"
                          class="position-absolute top-0 start-100 translate-middle p-1 bg-primary border border-light rounded-circle"
                        >
                          <span class="visually-hidden">{{ $t('rfq.filters.toggleFilters') }}</span>
                        </span>
                      </button>

                      <div v-show="showFilters" class="filter-panel shadow-sm">
                        <div class="mb-2">
                          <label class="form-label form-label-sm mb-1">{{ $t('rfq.fields.buyer') }}</label>
                          <MultiSelect
                            v-model="selectedBuyerIds"
                            :options="buyerOptions"
                            :placeholder="$t('rfq.filters.allBuyers')"
                            :searchable="true"
                            :end="true"
                            :simple-display="true"
                            button-class="form-select form-select-sm"
                            @close="onFilterChange"
                          />
                        </div>
                        <div class="mb-2">
                          <label class="form-label form-label-sm mb-1">{{ $t('rfq.fields.vessel') }}</label>
                          <MultiSelect
                            v-model="selectedVesselNames"
                            :options="vesselOptions"
                            :placeholder="$t('rfq.filters.allVessels')"
                            :searchable="true"
                            :end="true"
                            :simple-display="true"
                            button-class="form-select form-select-sm"
                            @close="onFilterChange"
                          />
                        </div>
                        <div class="d-flex justify-content-end gap-2 mt-2">
                          <button type="button" class="btn btn-sm btn-link text-decoration-none p-0 me-auto" :disabled="!hasActiveFilters" @click="clearFilters">
                            {{ $t('common.actions.clear') }}
                          </button>
                          <button type="button" class="btn btn-sm btn-secondary" @click="showFilters = false">
                            {{ $t('common.actions.close') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </DataGrid>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import quotationService from '@/services/quotationService'
import TrackingStatus from '@/constants/trackingStatus'
import DataGrid from '@/components/DataGrid.vue'
import MultiSelect from '@/components/MultiSelect.vue'
import { useDateFormatter } from '@/composables/useDateFormatter'

const router = useRouter()
const { t } = useI18n()
const { formatDate } = useDateFormatter()

const activeTab = ref('all')
const currentPage = ref(1)
const itemsPerPage = ref(10)
const requests = ref([])
const isLoading = ref(false)
const error = ref(null)
const totalItems = ref(0)
const searchText = ref('')
const sortField = ref('')
const sortDirection = ref('asc')

// Filter state — MultiSelect uses arrays
const showFilters = ref(false)
const filterDropdownRef = ref(null)
const selectedBuyerIds = ref([])
const selectedVesselNames = ref([])
const buyerOptions = ref([])
const vesselOptions = ref([])

const hasActiveFilters = computed(() => selectedBuyerIds.value.length > 0 || selectedVesselNames.value.length > 0)

const clearFilters = () => {
  selectedBuyerIds.value = []
  selectedVesselNames.value = []
  onFilterChange()
}

const handleClickOutside = (event) => {
  if (!showFilters.value || !filterDropdownRef.value) return
  // Ignore clicks inside the panel or inside a teleported MultiSelect menu
  if (filterDropdownRef.value.contains(event.target)) return
  if (event.target.closest?.('.ms-menu-teleport')) return
  showFilters.value = false
}

const statusCounts = ref({
  waiting: 0,
  submitted: 0,
  revision: 0,
  declined: 0,
  processing: 0,
  processedWithError: 0,
  readyToSend: 0,
  expired: 0
})

const columns = [
  { key: 'buyer', label: t('rfq.fields.buyer'), width: '12%', sortable: true, sortKey: 'buyerTenantName' },
  { key: 'vessel', label: t('rfq.fields.vessel'), width: '12%', sortable: true, sortKey: 'vesselName' },
  { key: 'rfqNo', label: t('rfq.fields.rfqNo'), width: '8%', sortable: true, sortKey: 'requestForQuotationCode' },
  { key: 'deliveryPort', label: t('rfq.fields.deliveryPort'), width: '8%', sortable: true, sortKey: 'deliveryPortDisplay' },
  { key: 'subject', label: t('rfq.fields.subject'), width: '10%', type: 'truncate', sortable: true, sortKey: 'subject' },
  { key: 'description', label: t('rfq.fields.description'), width: '11%', type: 'truncate', sortable: true, sortKey: 'description' },
  { key: 'hasAttachment', label: t('rfq.fields.attachment'), width: '5%', type: 'icon', iconClass: 'ti ti-paperclip f-18 text-primary', sortable: false },
  { key: 'itemCount', label: t('rfq.fields.itemCount'), width: '5%', sortable: true, sortKey: 'numberOfItems' },
  { key: 'dueDate', label: t('rfq.fields.dueDate'), width: '7%', sortable: true, sortKey: 'dueDate' },
  {
    key: 'status', label: t('rfq.fields.status'), width: '180px', type: 'badge', sortable: true, sortKey: 'trackingStatus',
    badgeMap: {
      'Waiting For Quotation': 'bg-light-warning',
      'Quotation Submitted': 'bg-light-success',
      'Revision Requested': 'bg-light-purple',
      'Declined': 'bg-light-danger',
      'Ready To Send': 'bg-light-success',
      'Expired': 'bg-light-orange'
    }
  }
]

const totalStatusCount = computed(() => Object.values(statusCounts.value).reduce((sum, v) => sum + v, 0))

const emptyStateMessage = computed(() => t(`rfq.emptyState.${activeTab.value}`))

// View-only tabs cannot edit/delete
const renderActions = computed(() => {
  if (activeTab.value === 'submitted' || activeTab.value === 'declined' || activeTab.value === 'expired') {
    return () => ['view']
  }
  return (item) => (item.status === 'Declined' || item.status === 'Expired') ? ['view'] : ['view', 'edit', 'delete']
})

const getCurrentTrackingStatus = () => {
  switch (activeTab.value) {
    case 'waiting': return [TrackingStatus.New]
    case 'submitted': return [TrackingStatus.ProcessedSuccessfully, TrackingStatus.ReadyToSendToNozzle]
    case 'revision': return [TrackingStatus.ReNew]
    case 'declined': return [TrackingStatus.Declined, TrackingStatus.DeclinedSuccessfully]
    case 'expired': return [TrackingStatus.Expired]
    default: return undefined
  }
}

const mapTrackingStatus = (trackingStatus) => {
  switch (trackingStatus) {
    case TrackingStatus.New: return 'Waiting For Quotation'
    case TrackingStatus.ProcessedSuccessfully: return 'Quotation Submitted'
    case TrackingStatus.ReNew: return 'Revision Requested'
    case TrackingStatus.ReadyToSendToNozzle: return 'Quotation Submitted'
    case TrackingStatus.Declined: return 'Declined'
    case TrackingStatus.DeclinedSuccessfully: return 'Declined'
    case TrackingStatus.Expired: return 'Expired'
    default: return 'Unknown Status'
  }
}

const fetchQuotations = async () => {
  isLoading.value = true
  error.value = null
  try {
    const searchParams = {
      page: currentPage.value,
      pageSize: itemsPerPage.value,
      searchText: searchText.value
    }
    if (sortField.value) {
      searchParams.sorting = `${sortField.value} ${sortDirection.value}`
    }
    const status = getCurrentTrackingStatus()
    if (status !== undefined) {
      searchParams.trackingStatus = status
    }
    if (selectedBuyerIds.value.length > 0) {
      searchParams.buyerTenantIds = selectedBuyerIds.value
    }
    if (selectedVesselNames.value.length > 0) {
      searchParams.vesselNames = selectedVesselNames.value
    }

    const response = await quotationService.searchQuotations(searchParams)
    if (response) {
      requests.value = response.items.map(item => ({
        id: item.id,
        buyer: item.buyerTenantName || '',
        vessel: item.vesselName || '',
        rfqNo: item.requestForQuotationCode || '',
        deliveryPort: item.deliveryPortDisplay || '',
        subject: item.subject || '',
        description: item.description || '',
        hasAttachment: item.hasAttachment || false,
        itemCount: item.numberOfItems || 0,
        dueDate: formatDate(item.dueDate),
        status: mapTrackingStatus(item.trackingStatus),
        trackingStatus: item.trackingStatus
      }))
      totalItems.value = response.rowCount || 0
      if (response.statusCounts) {
        statusCounts.value = {
          waiting: response.statusCounts.New || 0,
          submitted: response.statusCounts.ProcessedSuccessfully || 0,
          revision: response.statusCounts.ReNew || 0,
          readyToSend: response.statusCounts.ReadyToSendToNozzle || 0,
          declined: (response.statusCounts.Declined || 0) + (response.statusCounts.DeclinedSuccessfully || 0),
          processing: response.statusCounts.Processing || 0,
          processedWithError: response.statusCounts.ProcessedWithError || 0,
          expired: response.statusCounts.Expired || 0
        }
      }
    }
  } catch {
    error.value = t('rfq.errors.loadFailed')
  } finally {
    isLoading.value = false
  }
}

const loadBuyers = async () => {
  try {
    const data = await quotationService.searchBuyers({ searchText: '' })
    buyerOptions.value = (data?.items ?? []).map(b => ({ value: b.id, text: b.name }))
  } catch {
    buyerOptions.value = []
  }
}

const loadVessels = async () => {
  try {
    const data = await quotationService.searchVessels({ searchText: '' })
    vesselOptions.value = (data?.items ?? []).map(v => ({ value: v, text: v }))
  } catch {
    vesselOptions.value = []
  }
}

const onFilterChange = () => {
  currentPage.value = 1
  fetchQuotations()
}

const setActiveTab = (tab) => {
  activeTab.value = tab
  currentPage.value = 1
}

const handleSearch = () => {
  currentPage.value = 1
  fetchQuotations()
}
const handleSort = () => {
  currentPage.value = 1
  fetchQuotations()
}
const handlePageSizeChange = () => {
  currentPage.value = 1
  fetchQuotations()
}
const handlePageChange = () => {
  fetchQuotations()
}

const viewRequest = (item) => router.push(`/rfq/${item.id}`)
const editRequest = (item) => {
  if (item.status === 'Quotation Submitted' || item.status === 'Declined' || item.status === 'Ready To Send') {
    router.push(`/rfq/${item.id}`)
  } else {
    router.push(`/rfq/${item.id}?edit=true`)
  }
}
const deleteRequest = async (item) => {
  const confirmed = confirm(t('rfq.confirmDelete'))
  if (confirmed) {
    try {
      await quotationService.deleteQuotation(item.id)
      fetchQuotations()
    } catch {
      alert(t('rfq.errors.deleteFailed'))
    }
  }
}

watch(activeTab, () => fetchQuotations())
watch(itemsPerPage, () => {
  currentPage.value = 1
  fetchQuotations()
})

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  await Promise.all([loadBuyers(), loadVessels()])
  fetchQuotations()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Status pill on one line; the column is wide enough for the longest label. */
:deep(.cell-content .badge) {
  white-space: nowrap;
  min-width: 0;
  min-height: 0;
  padding: 0.45em 0.7em;
}
.filter-dropdown {
  position: relative;
}
.filter-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1050;
  width: 280px;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e3e6ef;
  border-radius: 0.5rem;
  animation: filter-fade-in 0.15s ease;
}
@keyframes filter-fade-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

<style>
.bg-light-purple {
  background-color: rgba(138, 43, 226, 0.15) !important;
  color: #8a2be2 !important;
}
.badge.bg-light-purple {
  background-color: #8a2be2 !important;
  color: #ffffff !important;
}
.badge.bg-light-warning {
  background-color: #ffc107 !important;
  color: #212529 !important;
}
.badge.bg-light-success {
  background-color: #198754 !important;
  color: #ffffff !important;
}
.badge.bg-light-danger {
  background-color: #dc3545 !important;
  color: #ffffff !important;
}
</style>
